import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Route as ConfigRoute } from 'config';
import { setPageHead } from 'utils/dom';

import { CurrentUserProvider } from 'contexts/CurrentUser';
import { AddressesProvider } from 'contexts/Addresses';
import { UpcomingMenusProvider } from 'contexts/UpcomingMenus';
import { NotificationsProvider } from 'contexts/Notifications';
import { FavoritesProvider } from 'contexts/Favorites';
import { SubscriptionProvider } from 'contexts/Subscription';

// Layouts
import LoggedIn from './layouts/LoggedIn';
import SignUp from './layouts/SignUp';

// Logged in pages
import UpcomingMenus from '../UpcomingMenus';
import OrderHistory from '../OrderHistory';
import Profile from '../Profile';
import Favorites from '../Favorites';
import Cart from '../Cart';
import RedeemGift from '../RedeemGift';
import Giveaway from '../Giveaway';
import TrackingPage from '../TrackingPage';
import Refer from '../Refer';
import ManageSubscription from '../ManageSubscription';
import NotificationPreferencePage from '../MyAccount/NotificationPreferencePage';
import PaymentMethod from '../MyAccount/PaymentMethod';
import AccountSettings from '../MyAccount/AccountSettings';
import DeliveryAddress from '../MyAccount/DeliveryAddress';
import SubscriptionPage from '../MyAccount/SubscriptionPage';

// Signup pages
import Plan from '../SignUp/Plan';
import Checkout from '../SignUp/Checkout';

function App(): ReactElement {
  const location = useLocation();
  const isAccountManagementEnabled = window.gon?.featureFlags?.isAccountManagementEnabled;

  useEffect(() => {
    setPageHead(location.pathname);
  }, [location]);

  return (
    <CurrentUserProvider>
      <AddressesProvider>
        <Routes>
          <Route path="/" element={<LoggedIn />}>
            <Route
              path={ConfigRoute.UPCOMING_MENUS}
              element={(
                <UpcomingMenusProvider>
                  <NotificationsProvider>
                    <UpcomingMenus />
                  </NotificationsProvider>
                </UpcomingMenusProvider>
              )}
            />

            <Route
              path={ConfigRoute.ORDER_HISTORY}
              element={<OrderHistory />}
            />

            {!isAccountManagementEnabled && (
              <Route
                path={ConfigRoute.PROFILE}
                element={(
                  <NotificationsProvider>
                    <Profile />
                  </NotificationsProvider>
                )}
              />
            )}

            <Route
              path={ConfigRoute.MY_FAVORITES}
              element={(
                <FavoritesProvider>
                  <Favorites />
                </FavoritesProvider>
              )}
            />

            <Route
              path={ConfigRoute.REDEEM_CODE}
              element={<RedeemGift />}
            />

            <Route
              path={ConfigRoute.GIVEAWAYS}
              element={<Giveaway />}
            />

            <Route
              path={ConfigRoute.TRACKING}
              element={<TrackingPage />}
            />

            <Route
              path={ConfigRoute.REFER}
              element={<Refer />}
            />
          </Route>
          {isAccountManagementEnabled && (
            <Route
              path="/"
              element={(
                <SubscriptionProvider>
                  <UpcomingMenusProvider>
                    <LoggedIn isAccountPage />
                  </UpcomingMenusProvider>
                </SubscriptionProvider>
              )}
            >
              <Route
                path={ConfigRoute.PROFILE}
                element={(
                  <NotificationsProvider>
                    <SubscriptionProvider>
                      <UpcomingMenusProvider>
                        <SubscriptionPage />
                      </UpcomingMenusProvider>
                    </SubscriptionProvider>
                  </NotificationsProvider>
                        )}
              />
              <Route
                path={ConfigRoute.ACCOUNT_SETTINGS}
                element={(
                  <AccountSettings />
                )}
              />

              <Route
                path={ConfigRoute.PAYMENT_METHOD}
                element={(
                  <PaymentMethod />
                )}
              />

              <Route
                path={ConfigRoute.DELIVERY_ADDRESS}
                element={(
                  <NotificationsProvider>
                    <DeliveryAddress />
                  </NotificationsProvider>
                )}
              />

              <Route
                path={ConfigRoute.NOTIFICATION_PREFERENCES}
                element={(
                  <NotificationPreferencePage />
                )}
              />
            </Route>
          )}

          <Route
            path={ConfigRoute.CARTS}
            element={<Cart />}
          />

          <Route
            path={ConfigRoute.CARTS_LEGACY}
            element={<Cart />}
          />

          <Route
            path="/"
            element={(
              <LoggedIn hasSidebar={false} />
            )}
          >
            <Route
              path={ConfigRoute.MANAGE_SUBSCRIPTION}
              element={(
                <UpcomingMenusProvider>
                  <ManageSubscription />
                </UpcomingMenusProvider>
              )}
            />
          </Route>

          <Route
            path={ConfigRoute.SIGNUP_PLAN}
            element={<SignUp />}
          >
            <Route
              path="plan"
              element={(
                <SubscriptionProvider>
                  <Plan />
                </SubscriptionProvider>
              )}
            />
          </Route>

          <Route
            path={ConfigRoute.SIGNUP_CHECKOUT}
            element={<SignUp />}
          >
            <Route
              path=":step"
              element={(
                <SubscriptionProvider>
                  <Checkout />
                </SubscriptionProvider>
              )}
            />
          </Route>

          <Route
            path={ConfigRoute.SIGNUP_CHECKOUT_REDEEM}
            element={<SignUp />}
          >
            <Route
              path="gift/:step"
              element={(
                <SubscriptionProvider>
                  <Checkout />
                </SubscriptionProvider>
              )}
            />
          </Route>

          <Route
            path={ConfigRoute.SIGNUP_CHECKOUT_REDEEM}
            element={<SignUp />}
          >
            <Route
              path="giveaway/:step"
              element={(
                <SubscriptionProvider>
                  <Checkout />
                </SubscriptionProvider>
              )}
            />
          </Route>
        </Routes>
      </AddressesProvider>
    </CurrentUserProvider>
  );
}

export default App;
